/**
 * Audit Console Dashboard Plugin — entry point and registration.
 *
 * Moves the superuser-only Audit Console (audit record list + usage report;
 * user-activity-audit-log and audit-usage-report specs) from a top-level nav
 * rail item into the Dashboard section's selector. The content is the
 * existing `AuditLogViewer` rendered in embedded mode, so its internal
 * "Audit log" | "Usage Report" tabs, filters, dialogs, and access-denied
 * branches behave exactly as before; `DashboardShell` supplies the page
 * chrome (header/title) instead of the viewer's own full-page layout.
 *
 * Visibility: `visibleDashboardsFor` in `dashboardRegistry.ts` shows this
 * entry only to superusers (frontend defense in depth, audit-log spec
 * Req 9.1/9.2 and audit-usage-report Req 1.1/1.2); the server-side pipeline
 * resolvers remain the authoritative access gate for both views.
 */

import React from "react";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import type {
  DashboardPlugin,
  PluginDataResult,
  PluginFilterPanelProps,
  PluginReportContentProps,
} from "../../types";
import AuditLogViewer from "../../../AuditLogViewer";

/**
 * The Audit Console owns its filter controls inside `AuditLogViewer`, so the
 * shell-level filter panel renders nothing.
 */
const AuditConsoleFilterPanel: React.FC<PluginFilterPanelProps> = () => null;

/**
 * `AuditLogViewer` fetches its own data (`getAuditLogs` / `getUsageReport`),
 * so the shell-level data hook is inert: never loading, no error, nothing to
 * refetch. `data` is a non-null constant so DashboardShell's first-load
 * banner never shows.
 */
const AUDIT_CONSOLE_DATA: PluginDataResult = {
  data: {},
  isLoading: false,
  error: null,
  refetch: () => {},
};
const useAuditConsoleData = (): PluginDataResult => AUDIT_CONSOLE_DATA;

/**
 * The embedded viewer is always "open" while this plugin is selected;
 * DashboardComponent owns the nav-rail margin, hence `menuWidth={0}`.
 */
const AuditConsoleContent: React.FC<PluginReportContentProps> = () => (
  <AuditLogViewer isOpen embedded menuWidth={0} />
);

export const auditConsoleDashboard: DashboardPlugin = {
  id: "audit-console",
  displayName: "Audit Console",
  icon: <FactCheckOutlinedIcon />,
  description: "Superuser-only audit log records and usage report",

  FilterPanel: AuditConsoleFilterPanel,
  ReportContent: AuditConsoleContent,
  useData: useAuditConsoleData,

  // No AI summary, no second-table comparison, and no CSV export — the
  // shell hides the CSV button when a plugin declares no `exportCsv`.
  supportsComparison: false,
};
